import { motion, useReducedMotion, type MotionProps } from 'framer-motion';
import { forwardRef, useMemo, type ElementType, type ReactNode } from 'react';
import {
  fadeIn,
  fadeInUp,
  staggerContainer,
  staggerSlow,
  sectionViewport,
} from '../animations';

export type StaggerProps = {
  children: ReactNode;
  slow?: boolean;
  className?: string;
  as?: ElementType;
} & MotionProps;

/**
 * Parent orchestrator. Drives a reading-order cascade of StaggerItem
 * children at 0.06s (default) or 0.12s (slow) steps, once on inView.
 * Under reduced motion the children simply fade — no stagger delay.
 */
export function Stagger({
  children,
  slow = false,
  className,
  as = 'div',
  ...rest
}: StaggerProps) {
  const reduce = useReducedMotion();
  const MotionTag = useMemo(() => motion(as as ElementType), [as]);

  return (
    <MotionTag
      className={className}
      variants={reduce ? undefined : slow ? staggerSlow : staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={sectionViewport}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

type StaggerItemVariant = 'fadeInUp' | 'fadeIn';

const ITEM_VARIANTS = {
  fadeInUp,
  fadeIn,
} as const;

const REDUCED_ITEM = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
} as const;

export type StaggerItemProps = {
  children: ReactNode;
  variant?: StaggerItemVariant;
  as?: ElementType;
  className?: string;
} & MotionProps;

/**
 * Child of Stagger. Carries the per-child variants and inherits the
 * initial/whileInView orchestration from its Stagger parent.
 *
 * forwardRef so framer-motion's AnimatePresence/PopChild can attach its
 * measurement ref when StaggerItem children are presence-animated (e.g. the
 * filterable comparison rows). Without it React warns the ref is dropped.
 */
export const StaggerItem = forwardRef<HTMLElement, StaggerItemProps>(function StaggerItem(
  { children, variant = 'fadeInUp', as = 'div', className, ...rest },
  ref,
) {
  const reduce = useReducedMotion();
  const MotionTag = useMemo(() => motion(as as ElementType), [as]);

  return (
    <MotionTag
      ref={ref}
      className={className}
      variants={reduce ? REDUCED_ITEM : ITEM_VARIANTS[variant]}
      {...rest}
    >
      {children}
    </MotionTag>
  );
});
